import { FileImageOutlined, TeamOutlined } from "@ant-design/icons"
import { Select, type SelectProps } from "components/Select/Select"
import { Tag, type TagProps } from "components/Tag/Tag"

import { Image } from "components/Image/Image"
import { SearchInput } from "components/SearchInput/SearchInput"
import { Table, type ExpandableConfig } from "components/Table/Table"
import { useTableParams } from "components/Table/useTableParams"
import { Visit, VISIT_STATUSES, VISIT_TYPES } from "interface/visit"
import { memoize } from "lodash-es"
import { ComponentProps, memo, ReactNode, useCallback, useMemo } from "react"
import { useAdminTeams } from "services/teams"
import { useAdminVisits } from "services/visits"
import { cn } from "styles/cn"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { asDate, DATE_TIME_FORMAT } from "utils/date"
import { formatVisitStatus, visitStatusDate } from "utils/visits"
import { RequestVisit } from "./RequestVisit"
import { UpdateVisit } from "./UpdateVisit"

export const AdminVisits = () => {
	const { set, page, sort, query, status, type, fieldSupportTeamId } =
		useTableParams({
			sort: ["updatedAt-descending"],
			status: [],
			type: [],
			fieldSupportTeamId: [],
		})
	const { data: { result: visits = [], total } = {}, isFetching } =
		useAdminVisits({
			status: status as Array<Visit["status"]>,
			type: type as Array<Visit["visitType"]>,
			sort,
			page,
			query,
			fieldSupportTeamId,
		})
	const handleTableChange = useCallback<
		NonNullable<TableVisitsType["onChange"]>
	>(
		(pagination, _, sorter) => {
			const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter
			set(
				["page", String(pagination.current)],
				["sort", field && order ? [`${field}-${order}ing`] : []],
			)
		},
		[set],
	)
	const handleSearch = useCallback<
		NonNullable<ComponentProps<typeof SearchInput>["onSearch"]>
	>((value) => set(["query", value]), [set])

	return (
		<div className="flex h-full min-h-0 flex-col gap-3">
			<div className="flex justify-between gap-3">
				<h2 className="font-nunito text-3xl font-semibold">Visits</h2>
				<RequestVisit />
			</div>

			<div className="flex h-full min-h-0 flex-col gap-2">
				<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 2xl:grid-cols-[minmax(20rem,2fr)_repeat(3,minmax(12rem,1fr))]">
					<SearchInput
						size="large"
						value={query}
						className="min-w-0"
						placeholder="Search by patient name, or visit, patient, enrollment or team ID"
						onSearch={handleSearch}
					/>
					<Select<string[]>
						size="large"
						mode="multiple"
						virtual={false}
						allowClear
						maxTagCount="responsive"
						aria-label="Status"
						placeholder="Select status"
						className="min-w-0"
						value={status as string[]}
						options={STATUS_OPTIONS}
						onChange={(value) => set(["status", value])}
					/>
					<Select<string[]>
						size="large"
						mode="multiple"
						virtual={false}
						allowClear
						maxTagCount="responsive"
						aria-label="Visit type"
						placeholder="Select visit type"
						className="min-w-0"
						value={type as string[]}
						options={TYPE_OPTIONS}
						onChange={(value) => set(["type", value])}
					/>
					<SelectTeam
						size="large"
						value={fieldSupportTeamId as string[]}
						onChange={(value) => set(["fieldSupportTeamId", value])}
					/>
				</div>

				<Table<Visit>
					dataSource={visits}
					columns={COLUMNS}
					rowKey="id"
					loading={isFetching}
					expandable={EXPANDABLE}
					pagination={{
						total,
						current: Number(page),
					}}
					onChange={handleTableChange}
					onRow={({ patient }) => ({
						"aria-label": `Visit for ${patient?.fullName ?? "unknown patient"}`,
					})}
				/>
			</div>
		</div>
	)
}

const SelectTeam = memo(function SelectTeam({
	className,
	...props
}: SelectProps<string[]>) {
	const { data: teams = [], isFetching } = useAdminTeams()
	const options = useMemo(
		() => [
			{ value: "none", label: "No team" },
			...teams.map(({ id: value, name: label }) => ({ value, label })),
		],
		[teams],
	)

	return (
		<Select<string[]>
			{...props}
			loading={isFetching}
			mode="multiple"
			virtual={false}
			allowClear
			maxTagCount="responsive"
			aria-label="Field support team"
			placeholder="Select team"
			className={cn("min-w-0", className)}
			optionFilterProp="label"
			prefix={<TeamOutlined aria-hidden />}
			options={options}
		/>
	)
})

const getStatusColor = memoize((status: Visit["status"]): TagProps["color"] => {
	if (status === "UNSCHEDULED") {
		return "gold"
	} else if (status === "SCHEDULED") {
		return "green"
	} else if (status === "COMPLETED") {
		return "blue"
	} else if (status === "CANCELLED") {
		return "red"
	}
	return undefined
})

const DATE_TIME_ZONE_FORMAT = `${DATE_TIME_FORMAT} z`

const renderDate = memoize((value?: string) =>
	value ? asDate(value).format(DATE_TIME_ZONE_FORMAT) : DEFAULT_PLACEHOLDER,
)

const COLUMNS: TableVisitsType["columns"] = [
	{
		key: "operation",
		title: "Actions",
		fixed: "left",
		render(_, visit) {
			return <UpdateVisit visit={visit} />
		},
	},
	{
		dataIndex: "patient",
		key: "patient",
		title: "Patient",
		render(value: Visit["patient"]) {
			return value?.fullName ?? DEFAULT_PLACEHOLDER
		},
	},
	{
		dataIndex: "id",
		key: "id",
		title: "Visit ID",
	},
	{
		dataIndex: "visitType",
		key: "visitType",
		title: "Visit type",
		sorter: true,
	},
	{
		dataIndex: "status",
		key: "status",
		title: "Status",
		sorter: true,
		render(_, visit) {
			return (
				<Tag color={getStatusColor(visit.status)}>
					{formatVisitStatus(visit.status)}
				</Tag>
			)
		},
	},
	{
		dataIndex: "relevantDate",
		key: "relevantDate",
		title: "Date",
		sorter: true,
		render(_, visit) {
			return renderDate(visitStatusDate(visit))
		},
	},
	{
		dataIndex: "fieldSupportTeam",
		key: "fieldSupportTeam",
		title: "Team",
		render(value: Visit["fieldSupportTeam"]) {
			return value?.name ?? DEFAULT_PLACEHOLDER
		},
	},
	{
		dataIndex: "installationPhotoUrl",
		key: "installationPhotoUrl",
		title: "Photo",
		render(value: Visit["installationPhotoUrl"], visit) {
			if (!value) {
				return (
					<span className="flex items-center gap-1 text-navy-60">
						<FileImageOutlined aria-hidden />
						No photo
					</span>
				)
			}
			return (
				<Image
					src={value}
					preview
					rootClassName="aspect-square rounded-md"
					className="size-10 rounded-md object-cover"
					alt={`Installation photo for ${visit.patient?.fullName ?? "visit"}`}
				/>
			)
		},
	},
]

const EXPANDABLE: ExpandableConfig<Visit> = {
	expandedRowRender: (visit) => {
		const details: Array<[string, ReactNode]> = [
			["ID", visit.id],
			["Patient user ID", visit.patient?.id ?? DEFAULT_PLACEHOLDER],
			[
				"Field support team ID",
				visit.fieldSupportTeam?.id ?? DEFAULT_PLACEHOLDER,
			],
			["Enrollment ID", visit.enrollmentId ?? DEFAULT_PLACEHOLDER],
			["Created at", renderDate(visit.createdAt)],
			["Updated at", renderDate(visit.updatedAt)],
			["Visit date", renderDate(visit.visitDate)],
			["Completed date", renderDate(visit.completedDate)],
			["Cancel date", renderDate(visit.cancelDate)],
			["Status", visit.status],
			[
				"Visit duration",
				visit.visitDurationInMinutes
					? `${visit.visitDurationInMinutes} min`
					: DEFAULT_PLACEHOLDER,
			],
		]

		return (
			<dl
				className="columns-xs space-y-2 text-navy-100"
				aria-label={`Visit details for ${visit.patient?.fullName ?? "unknown patient"}`}
			>
				{details.map(([title, description]) => (
					<dt key={title} className="flex items-start gap-2">
						<span className="text-nowrap text-navy-60">{title}:</span>
						{description}
					</dt>
				))}
			</dl>
		)
	},
}

const STATUS_OPTIONS = VISIT_STATUSES.map((status) => ({
	value: status,
	label: formatVisitStatus(status),
}))

const TYPE_OPTIONS = VISIT_TYPES.map((type) => ({ value: type, label: type }))

type TableVisitsType = ComponentProps<typeof Table<Visit>>
